import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import { useLanguage } from '../../../../Context/LanguageContext';
import { useTheme } from '../../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../../Styles/theme';

import { ReactComponent as MenuSvg } from '../../../../image/Menu.svg';

const FailedSubscriptMembershipInfoView = forwardRef((props, ref) => {
  const { t, isEng } = useLanguage();
  const {
    data,
    index,
    size,
    retryClick,
    deleteClick,
  } = props;
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [isClicked, setIsClicked] = useState(false);
  const [fontColor, setFontColor] = useState(theme.subText);
  const [border, setBorder] = useState(`1px solid ${theme.subBackground}`);
  const [hoverBorder, setHoverBorder] = useState(`1px solid ${theme.subBackground}`);
  const [backgroundColor, setBackgroundColor] = useState(theme.subBackground);

  const OptionBoxRef = useRef();
  const OptionButtonRef = useRef();
  const OptionRetryRef = useRef();
  const OptionDeleteRef = useRef();

  useImperativeHandle(ref, () => ({
    setIsClicked: () => {
      setIsClicked(false);
    },
    getButtonRef: () => {
      return OptionButtonRef.current;
    },
    getBoxRef: () => {
      return OptionBoxRef.current;
    }
  }))

  useEffect(() => {
    if (data.isExpectedMemberShip) return;

    if (isClicked) {
      OptionBoxRef.current.style.height = `64px`;
      OptionBoxRef.current.style.border = `1px solid ${theme.primary}`;
    }
    else {
      OptionBoxRef.current.style.height = `0px`;
      OptionBoxRef.current.style.border = `0px solid ${theme.primary}`;
    }
  }, [isClicked, data, theme])

  // 외부 클릭 감지 등록 //
  useEffect(() => {
    document.addEventListener("mousedown", onClickOutSide);

    return () => {
      document.removeEventListener("mousedown", onClickOutSide);
    }
  }, [])

  useEffect(() => {
    setFontColor(theme.pointRed);
    setBorder(`1px solid ${theme.subBackground}`);
    setHoverBorder(`1px solid ${theme.primary}`);
    setBackgroundColor(theme.subBackground);
  }, [data, theme])

  function onClickOutSide(e) {
    if (OptionBoxRef.current !== e.target
      && !OptionBoxRef.current.contains(e.target)
      && OptionButtonRef.current !== e.target
      && !OptionButtonRef.current.contains(e.target)
      && OptionRetryRef.current !== e.target
      && OptionDeleteRef.current !== e.target) {
      setIsClicked(false);
    }
  }

  // 결제 금액 정리 (가격 prefix만 반환) //
  function decideTotalPriceView() {
    if (data.selectedPaymentOptions === undefined || data.selectedPaymentOptions.length === 0) {
      if (data.cardPrice === 0) return data.cashPrice.toLocaleString('ko-KR') + ' - ';
      else if (data.cashPrice === 0) return data.cardPrice.toLocaleString('ko-KR') + ' - ';
      else return (parseInt(data.cashPrice) + parseInt(data.cardPrice)).toLocaleString('ko-KR') + ' - ';
    }
    else {
      const totalPrice = data.selectedPaymentOptions.reduce((sum, item) => sum + item.price, 0);
      return totalPrice.toLocaleString('ko-KR') + ' - ';
    }
  }

  // 남은일수에 맞춰 텍스트 정리 //
  function remainDaysView() {
    if (data.isRefund) return t('memberDetail.statusRefund');
    else if (data.isExpectedMemberShip) return t('memberDetail.statusPaymentPending');
    else if (data.totalMax === data.courseTakeCount
      && (data.lastReserveDate !== undefined && (moment().startOf('day').isAfter(data.lastReserveDate)))) return t('memberDetail.statusExpired');
    else if (moment().isBefore(data.startDate)) return t('memberDetail.statusScheduled');
    else if (moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'days') > 0) {
      return moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'days') + t('memberDetail.daysSuffix');
    }
    else if (moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'days') === 0) {
      return 'D-Day'
    }

    return t('memberDetail.statusExpired');
  }


  // 수강 횟수 표시 //
  function decideUseTotalMax() {
    if (data.totalMax === -1) return data.courseTakeCount + '/∞';
    else return data.courseTakeCount + '/' + data.totalMax;
  }
  
  function mouseOver() {
    OptionButtonRef.current.style.opacity = 1;
  }

  function mouseLeave() {
    OptionButtonRef.current.style.opacity = 0;
  }

  // 몸체 눌렀을때 켜지기 //
  function onClickBody(e) {
  }

  return (
    <TopLevelWrapper>
      <MemberShipComponent
        $isDark={isDark}
        zIndex={80 + size - index}
        border={border}
        hoverBorder={hoverBorder}
        backgroundColor={backgroundColor}
        onMouseOver={mouseOver}
        onMouseLeave={mouseLeave}
        onClick={onClickBody}>
        <Table>
          <Body>
            <Tr style={{ width: '280px' }}>
              <Td>
                <TextWrapper style={{ maxWidth: '280px', gap: '5px' }}>
                  {
                    data.isSubscriptionMembership &&
                    <SubscriptionBox status={data.subscribeStatus && !data.subscribeDeleted}>
                      <SubscriptionText>{(data.subscribeStatus && !data.subscribeDeleted) ? t('memberDetail.statusSubscribing') : t('memberDetail.statusSubscriptionEnded')}</SubscriptionText>
                    </SubscriptionBox>
                  }
                  <Text color={fontColor}>{data.memberShipName}</Text>
                </TextWrapper>
              </Td>
            </Tr>
            <Tr style={{ width: '145px' }}>
              <Td>
                <TextWrapper style={{ maxWidth: '135px' }}>
                  <Text color={fontColor}>
                    {isEng
                      ? `${moment(data.startDate).format(t('partnerGym.history.dateFormat'))} - ${moment(data.endDate).format(t('partnerGym.history.dateFormat'))}`
                      : `${data.startDate.slice(2, 10).replaceAll('-', '.')} - ${data.endDate.slice(2, 10).replaceAll('-', '.')}`
                    }
                  </Text>
                </TextWrapper>
              </Td>
            </Tr>
            <Tr style={{ width: '190px' }}>
              <Td>
                <TextWrapper style={{ maxWidth: '180px' }}>
                  <Text color={fontColor} style={{ flexShrink: 0 }}>{decideTotalPriceView()}</Text>
                  <ReasonText color={fontColor}>{t('memberDetail.statusPaymentFailed')}</ReasonText>
                  {/*<ReasonText color={fontColor}>{data.terminationReason}</ReasonText>*/}
                </TextWrapper>
              </Td>
            </Tr>
            <Tr style={{ width: '80px' }}>
              <Td>
                <TextWrapper style={{ maxWidth: '70px' }}>
                  <Text color={fontColor}>{data.notReceivedPrice}</Text>
                </TextWrapper>
              </Td>
            </Tr>
            <Tr style={{ width: '4px' }} />
            <Tr style={{ width: '55px' }}>
              <Td>
                <TextWrapper style={{ maxWidth: '55px' }}>
                  <Text color={fontColor}>{remainDaysView()}</Text>
                </TextWrapper>
              </Td>
            </Tr>
            <Tr style={{ width: '55px' }}>
              <Td>
                <TextWrapper style={{ maxWidth: '55px' }}>
                  <Text color={fontColor}>{decideUseTotalMax()}</Text>
                </TextWrapper>
              </Td>
            </Tr>
            <Tr>
              <Td>
                <OptionButton ref={OptionButtonRef} onClick={() => setIsClicked(!isClicked)} />
                <OptionWrapper ref={OptionBoxRef}>
                  <Option ref={OptionRetryRef} onMouseDown={() => retryClick(index)}>{t('memberDetail.btnRetryPayment')}</Option>
                  <Option ref={OptionDeleteRef} onMouseDown={() => deleteClick(index)}>{t('memberDetail.btnDelete')}</Option>
                </OptionWrapper>
              </Td>
            </Tr>
          </Body>
        </Table>
      </MemberShipComponent>
    </TopLevelWrapper>
  )
});

export default FailedSubscriptMembershipInfoView;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 1018px;
`;

const MemberShipComponent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  width: 970px;
  height: 52px;

  // box-shadow: ${props => props.$isDark ? 'none' : '0px 10px 50px rgba(240, 232, 227, 0.5)'};

  z-index: ${props => props.zIndex};

  border: ${props => props.border};
  border-radius: 30px;
  background-color: ${props => props.backgroundColor};
    
  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    border: ${props => props.hoverBorder};
  }
`;

const Table = styled.table`
  display: flex;
  flex-direction: row;
`;

const Body = styled.tbody`
  display: flex;
  flex-direction: row;

  align-items: center;
    
  margin-left: 15px;

  gap: 15px;
`;

const Tr = styled.tr`
  display: flex;
  flex-direction: row;
`;

const Td = styled.td`
  display: flex;
  flex-direction: row;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: flex-start;

  overflow: hidden;

  max-height: 20px;
`;

const ReasonText = styled.span`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.color};

  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  margin: 0 0 0 0;
`;

const Text = styled.span`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  padding: 0 0 0 0;
`;

const SubscriptionBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 0px 1px;
  margin-top: 2px;

  min-width: ${props => props.status ? `40px` : `54px`};
  min-height: 18px;

  border-radius: 5px;
  background-color: ${props => props.status ? props.theme.primary : props.theme.subKey};
`;

const SubscriptionText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 12px;

  color: ${props => props.theme.subBackground};

  margin: -2px 0 0 0;
  padding: 0 0 0 0;

  &:hover {
    cursor: default;
  }
`;

const OptionButton = styled(MenuSvg)`
  width: 21px;
  height: auto;
  opacity: 0;

  & rect {
    fill: ${props => props.theme.line};
  }

  transition: all 0.3s ease-in-out;
`;

const OptionWrapper = styled.div`
  position: absolute;

  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  overflow: hidden;
    
  z-index: 10;

  margin-top: 50px;
  margin-left: -20px;

  border-radius: 8px;

  transition: all 0.2s ease-in-out;
`;

const Option = styled.div`
  display: flex;
  flex-direction: column;;

  justify-content: center;
  align-items: center;

  width: 58px;
  height: 38px;

  margin: 0 0 0 0;

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.primary};
  background-color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    color: ${props => props.theme.white};
    background-color: ${props => props.theme.primary};
  }
`;
